import {
  Box,
  H4,
  MaterialTableWrapper,
  MRT_TableOptions,
  MRT_ColumnDef,
  MRT_Row,
  withThemeProvider,
  Flex,
  Button,
  OusterProvider,
  Tooltip,
} from '@ouster/webviz-react-components';
import type { MaterialTableWrapperProps } from '@ouster/webviz-react-components';

import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useStoreActions, useStoreState } from 'src/reducers';
import { ProvisionedOrganization } from 'src/reducers/adminDashboard';
import { getRowActionsColumnOptions } from 'src/utils/tableUtils';

import { formatDateLong } from '../utils';

export interface Organization {
  id: number;
  name: string;
  isProvisioned: boolean;
  members: number;
  recordingCount: number;
  createdAt: string; // When provisioning started
  activatedAt?: string; // When handover happened
  provisionerName?: string; // Name of FAE handling it
}

const ThemedMaterialTableWrapper =
  withThemeProvider<MaterialTableWrapperProps<ProvisionedOrganization>>(
    MaterialTableWrapper
  );

export default function ProvisioningOrganization({
  provisioningOrgs,
}: {
  provisioningOrgs: ProvisionedOrganization[];
}) {
  const { setCurUserOrg } = useStoreActions((actions) => actions.user);
  const { appUser } = useStoreState((storeState) => storeState.user);
  const navigate = useNavigate();
  const disabledTooltipLabel = 'Only the provisioning initiator can do this.';

  const inviteProvisionOrganizationUserHandler = (
    row: MRT_Row<ProvisionedOrganization>
  ) => {
    setCurUserOrg(row.original.organization_id);
    sessionStorage.setItem('orgChanged', 'true');
    sessionStorage.setItem('columnVisibilityChanged', 'true');

    navigate('/organization');
  };
  const enterProvisioningHandler = (row: MRT_Row<ProvisionedOrganization>) => {
    setCurUserOrg(row.original.organization_id);
    sessionStorage.setItem('orgChanged', 'true');
    sessionStorage.setItem('columnVisibilityChanged', 'true');

    navigate('/drive/private');
  };

  const columns = useMemo<MRT_ColumnDef<ProvisionedOrganization>[]>(
    () => [
      {
        accessorKey: 'organization_name',
        header: 'Client Organization',
      },
      {
        accessorKey: 'provisioned_by_name',
        header: 'Provisioned By',
      },
      {
        accessorKey: 'provisioned_date',
        header: 'Invited Date',
        Cell: ({ cell }) => formatDateLong(cell.getValue<string>()),
      },
      {
        accessorKey: 'member_count',
        header: 'Invited Members',
        Cell: ({ row }) => {
          const {
            member_count: memberCount,
            invitation_count: invitationCount,
          } = row.original;
          return memberCount + invitationCount - 1;
        },
      },
    ],
    []
  );

  const tableOptions: MRT_TableOptions<ProvisionedOrganization> = {
    columns,
    data: provisioningOrgs,
    initialState: {
      pagination: { pageSize: 10, pageIndex: 0 },
      columnPinning: { right: ['mrt-row-actions'], left: ['name'] },
      showGlobalFilter: true,
    },
    enableRowActions: true,
    positionGlobalFilter: 'right',
    enableSorting: true,
    enableToolbarInternalActions: false,
    positionActionsColumn: 'last',
    getRowId: (originalRow) => originalRow.organization_id.toString(),
    layoutMode: 'grid',
    displayColumnDefOptions: getRowActionsColumnOptions(252),
    muiTablePaperProps: {
      sx: {
        backgroundColor: 'transparent',
        boxShadow: 'none',
      },
    },
    muiTableContainerProps: {
      sx: {
        border: '1px solid',
        borderColor: 'rgba(255, 255, 255, 0.12)',
      },
    },
    muiTableBodyRowProps: {
      hover: false,
    },
    renderRowActions: ({ row }) => {
      const isDisabled = row.original.provisioned_by_name !== appUser?.name;
      const disabledStyle = isDisabled ? { opacity: 0.5 } : {};
      return (
        <OusterProvider>
          <Flex gap={2} alignItems="center">
            <Tooltip label={isDisabled ? disabledTooltipLabel : undefined}>
              <span>
                <Button
                  colorScheme="gray"
                  onClick={() => inviteProvisionOrganizationUserHandler(row)}
                  size="sm"
                  leftIcon="reinvite"
                  disabled={isDisabled}
                  style={disabledStyle}
                >
                  Invite
                </Button>
              </span>
            </Tooltip>
            <Tooltip label={isDisabled ? disabledTooltipLabel : undefined}>
              <span>
                <Button
                  colorScheme="gray"
                  onClick={() => enterProvisioningHandler(row)}
                  size="sm"
                  leftIcon="upload"
                  disabled={isDisabled}
                  style={disabledStyle}
                >
                  Upload Recording
                </Button>
              </span>
            </Tooltip>
          </Flex>
        </OusterProvider>
      );
    },
  };

  if (provisioningOrgs.length === 0) {
    return null;
  }

  return (
    <Box position="relative">
      <H4
        color="gray.300"
        fontSize={14}
        position="absolute"
        left={0}
        top={4}
        zIndex={2}
        display="flex"
        alignItems="center"
        gap={2}
      >
        <Box
          borderRadius="full"
          w={2}
          h={2}
          bg="yellow.300"
          display="inline-block"
        />
        <span style={{ lineHeight: 1 }}>
          Provisioning In Progress: {provisioningOrgs.length}
        </span>
      </H4>
      <Box zIndex={1}>
        <ThemedMaterialTableWrapper {...tableOptions} />
      </Box>
    </Box>
  );
}
